/**
 * This app's binding over the shared error standard in `@liminal/web-ui/errors`.
 *
 * The parser and the category tables live there, shared with nim-web and
 * ported from mobile. What this console shows differs from a consumer app in
 * one way: staff read it. The server's own words are the useful part of a
 * failure here ("announcement not found", "flag already enabled"), where the
 * consumer apps replace a technical category's message with product copy;
 * and the status and reason are what a bug report quotes, so they are
 * appended rather than hidden. The gateway sanitises a technical category's
 * message before it leaves the server (common/go/grpc/errorhandler.go), so
 * showing it leaks nothing.
 */

import { formatErrorMessage, parseError } from '@liminal/web-ui/errors';
import { isHttpError } from '@liminal/errors/http-errors';

/**
 * User-facing copy for a failed request in this console.
 *
 * Reads the message off the gateway's envelope when there is one, and the
 * shared category copy otherwise (a network failure says to check the
 * connection, not "Failed to fetch"). An HttpError carries its status and,
 * when the server named one, its taxonomy reason -- not the numeric gRPC
 * code the parser falls back to, which tells staff nothing.
 */
export function getErrorMessage(error: unknown): string {
  const parsed = parseError(error);
  const text =
    parsed.category === 'NETWORK' || !parsed.message ? formatErrorMessage(parsed) : parsed.message;

  if (!isHttpError(error)) return text;
  const reason = error.data?.error?.reason;
  return `${text} (HTTP ${error.status}${reason ? `, ${reason}` : ''})`;
}
